/**
 * sidecar-seed
 *
 * Build SidecarRunState from chat message annotations (for hydration from history).
 */

import type { ChatMessage } from '@/types'
import type {
  SidecarAgentState,
  SidecarExecutionMode,
  SidecarRunState,
  SidecarWorkerDefinition,
  SidecarWorkerSource
} from '@/types/sidecar'

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined
}

function readNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function readBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined
}

function readExecutionMode(value: unknown): SidecarExecutionMode | undefined {
  if (value === 'parallel' || value === 'sequential') return value
  return undefined
}

function readWorkerSource(value: unknown): SidecarWorkerSource {
  if (value === 'built_in_helper' || value === 'custom_helper') return value
  return 'ad_hoc'
}

function buildSeedAgent(
  worker: Record<string, unknown>,
  fallbackStatus: SidecarAgentState['status']
): SidecarAgentState | null {
  const id = readString(worker.id)
  const title = readString(worker.title)
  const objective = readString(worker.objective)

  if (!id || !title || !objective) return null

  const status = worker.status
  const normalizedStatus =
    status === 'completed' || status === 'error' || status === 'queued' || status === 'running'
      ? status
      : fallbackStatus

  return {
    id,
    title,
    objective,
    source: readWorkerSource(worker.source),
    ...(readString(worker.helperTemplateId)
      ? { helperTemplateId: readString(worker.helperTemplateId) }
      : {}),
    ...(readString(worker.helperId) ? { helperId: readString(worker.helperId) } : {}),
    deliverable: readString(worker.deliverable),
    status: normalizedStatus,
    finalOutput: readString(worker.finalOutput),
    error: readString(worker.error),
    toolTimeline: [],
    annotations: {}
  }
}

export function buildSeedRunFromAnnotation(options: {
  annotation: unknown
  chatId?: string | null
  createdAt?: string | number | null
}): SidecarRunState | null {
  const annotation = options.annotation
  if (!annotation || typeof annotation !== 'object' || Array.isArray(annotation)) {
    return null
  }

  const rawRun = (annotation as Record<string, unknown>).sidecar_run

  if (!rawRun || typeof rawRun !== 'object' || Array.isArray(rawRun)) {
    return null
  }

  const run = rawRun as Record<string, unknown>
  const runId = readString(run.runId)
  if (!runId) return null

  const status = run.status
  const normalizedStatus =
    status === 'planning' ||
    status === 'running' ||
    status === 'completed' ||
    status === 'error' ||
    status === 'cancelled'
      ? status
      : 'completed'

  const plannedWorkers = Array.isArray(run.plannedWorkers)
    ? run.plannedWorkers
        .map((worker) => {
          if (!worker || typeof worker !== 'object' || Array.isArray(worker)) return null
          const record = worker as Record<string, unknown>
          const id = readString(record.id)
          const title = readString(record.title)
          const objective = readString(record.objective)

          if (!id || !title || !objective) return null

          const deliverable = readString(record.deliverable)
          const dependsOn = Array.isArray(record.dependsOn)
            ? record.dependsOn
                .map((dependencyId) => readString(dependencyId) || '')
                .filter(Boolean)
            : []
          const def: SidecarWorkerDefinition = {
            id,
            title,
            objective,
            source: readWorkerSource(record.source),
            ...(readString(record.helperTemplateId)
              ? { helperTemplateId: readString(record.helperTemplateId) }
              : {}),
            ...(readString(record.helperId)
              ? { helperId: readString(record.helperId) }
              : {}),
            ...(dependsOn.length > 0 ? { dependsOn } : {}),
            ...(deliverable !== undefined && { deliverable })
          }
          return def
        })
        .filter(
          (worker): worker is SidecarWorkerDefinition => worker !== null
        )
    : []

  const workerSummaries = Array.isArray(run.workers)
    ? run.workers
        .map((worker) => {
          if (!worker || typeof worker !== 'object' || Array.isArray(worker)) return null
          return buildSeedAgent(worker as Record<string, unknown>, 'completed')
        })
        .filter((worker): worker is SidecarAgentState => Boolean(worker))
    : []

  const agents: Record<string, SidecarAgentState> = {}
  const agentOrder: string[] = []
  const plannedWorkerStatus: SidecarAgentState['status'] =
    normalizedStatus === 'completed'
      ? 'completed'
      : normalizedStatus === 'error' || normalizedStatus === 'cancelled'
        ? 'error'
        : 'queued'

  for (const worker of plannedWorkers) {
    agentOrder.push(worker.id)
    agents[worker.id] = {
      ...worker,
      status: plannedWorkerStatus,
      toolTimeline: [],
      annotations: {}
    }
  }

  for (const w of workerSummaries) {
    if (!w) continue
    const worker = w
    if (!agentOrder.includes(worker.id)) {
      agentOrder.push(worker.id)
    }

    agents[worker.id] = {
      ...(agents[worker.id] ?? {
        id: worker.id,
        title: worker.title,
        objective: worker.objective,
        deliverable: worker.deliverable,
        status: worker.status,
        toolTimeline: [],
        annotations: {}
      }),
      ...worker
    }
  }

  const completedAt = readNumber(run.completedAt)
  const startedAt = readNumber(run.startedAt)
  const createdAt = new Date(options.createdAt || Date.now()).getTime()

  return {
    runId,
    chatId: options.chatId ?? '',
    status: normalizedStatus,
    executionMode:
      readExecutionMode(run.executionMode) ??
      readExecutionMode(run.execution_mode),
    objective: readString(run.objective),
    reason: readString(run.reason),
    message: readString(run.message),
    agentCount: readNumber(run.agentCount) ?? agentOrder.length,
    plannedWorkers,
    successfulAgents: readNumber(run.successfulAgents),
    failedAgents: readNumber(run.failedAgents),
    finalAnswer: readString(run.finalAnswer),
    usedDeterministicFallback: readBoolean(run.usedDeterministicFallback),
    startedAt: startedAt ?? completedAt ?? createdAt,
    ...(normalizedStatus === 'completed' ||
    normalizedStatus === 'error' ||
    normalizedStatus === 'cancelled'
      ? { completedAt: completedAt ?? createdAt }
      : {}),
    agentOrder,
    agents
  }
}

export function buildSeedRunFromMessage(message: ChatMessage): SidecarRunState | null {
  return buildSeedRunFromAnnotation({
    annotation: message.message.annotation,
    chatId: message.message.chat_id,
    createdAt: message.message.created_at
  })
}
